/*TableBase.tsx component and its functionality. This is a sophisticated React Native table component
 with many features. let me break it down:
// Key features
- Data display with sorting, filtering, and pagination
- Unit conversion capabilities
- Column visibility toggle
- Search functionality
- Responsive design with theme support
a. Table Header Section:
- Title display
- Column visibility toggle menu
- Expand button (optional)

b. Column Headers:
- Sortable column titles
- Unit display and conversion
- Filter inputs per column

c. Data Rows:
- Displays data with proper formatting
- Handles unit conversions on display
- Supports row click eventsa. Data Processing:
- handleSort(): Manages column sorting (asc/desc)
- handleFilter(): Applies column-specific filters
- handleSearch(): Global search across visible columns
- handleUnitClick(): Manages unit conversion modal
- processedData: Memoized data processing with sorts and filters
- filteredData: Memoized search results

b. Unit Conversion:
- convertValueWithUnit(): Converts values between different units
- getMeasureFromUnit(): Gets the measurement type for a unit

c. UI Management:
- toggleColumnVisibility(): Shows/hides columns
- renderCell(): Renders individual data cells
- renderHeader(): Renders column headers with sort/filterconst [state, setState] = useState<TableState>({
    currentPage: 1,
    pageSize: pageSize,
    sortColumn: defaultSortColumn || '',
    sortDirection: defaultSortDirection === 'desc' ? 'descending' : 'ascending',
    filters: {},
    visibleColumns: defaultVisibleColumns || columns.map(col => col.key),
    columnUnits: {},
    searchQuery: '',
    data: data || [],
    rowsPerPage: pageSize
});
});const theme = useTheme();



// 1. Imports and Dependencies
- React and React Native core components
- React Native Paper UI components
- Custom types and utilities
- Unit conversion library

// 2. Utility Functions
getMeasureFromUnit():
- Purpose: Determines the measurement type (length, mass, etc.) for a given unit
- Used for unit conversion validation

convertValueWithUnit():
- Purpose: Converts numeric values between different units (e.g., m to cm)
- Includes error handling and debug logging
- Returns original value if conversion fails

// 3. Main Component: TableBase
Props:
- columns: Column definitions with titles, keys, and units
- data: Array of data to display
- title: Table title
- defaultVisibleColumns: Initially visible columns
- pageSize: Number of rows per page
- defaultSortColumn/Direction: Initial sorting
- onRowClick: Row click handler
- OnExpandedButtonClick: Expand view handler

// 4. State Management
Main state includes:
- currentPage: Current page number
- pageSize: Rows per page
- sortColumn/Direction: Current sorting
- filters: Column filters
- visibleColumns: Currently visible columns
- columnUnits: Unit conversion settings
- searchQuery: Global search text

// 5. Key Features
a. Unit Conversion:
- handleUnitClick(): Opens unit selection modal
- handleUnitSelect(): Processes unit conversion
- Converts all values in a column when unit changes

b. Data Handling:
- handleSort(): Column sorting
- handleFilter(): Column filtering
- handleSearch(): Global search
- processedData: Memoized data processing
- filteredData: Search results

c. UI Components:
- Column headers with sort indicators
- Filter inputs per column
- Unit display and conversion UI
- Pagination controls
- Column visibility toggle
*/




import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { DataTable, Text, IconButton, useTheme, Surface, Button } from 'react-native-paper';
import { TableProps, TableState, ColumnDefinition, Unit as TableUnit } from '../../types/table';
import convert, { Unit as ConvertUnit, Measure } from 'convert-units';
import { UnitModal } from './UnitModal';
import { ColumnVisibilityModal } from './ColumnVisibilityModal';
import Cookies from 'js-cookie';

const cookies = Cookies;

/**
 * Get the measure type from a given unit.
 * @param unit - The unit to check (e.g., 'm', 'kg', 'l').
 * @returns The measure type (e.g., 'length', 'mass', 'volume') or undefined if not found.
 */
const getMeasureFromUnit = (unit: ConvertUnit): Measure | undefined => {
    try {
        const measures = convert().measures();
        for (const measure of measures) {
            const possibilities = convert().possibilities(measure);
            if (possibilities.includes(unit)) {
                return measure;
            }
        }
    } catch (error) {
        console.warn('Error getting measure from unit:', error);
    }
    return undefined;
};

/**
 * Converts a value from one unit to another using the convert-units package.
 * @param value - The numerical value to convert.
 * @param fromUnit - The unit of the input value (e.g., 'm', 'kg', 'l').
 * @param toUnit - The unit to convert to (e.g., 'cm', 'g', 'ml').
 * @returns The converted value or the original value if conversion fails.
 */
const convertValueWithUnit = (value: number, fromUnit: ConvertUnit, toUnit: ConvertUnit): number => {
    //debugLog('Converting value with units:', { value, fromUnit, toUnit });

    // Return original value if units are the same
    if (fromUnit === toUnit) {

        return value;
    }

    try {
        // Create a new converter instance
        const converted = convert(value).from(fromUnit).to(toUnit);

        return Number(converted.toFixed(2));
    } catch (error) {
        console.warn('Conversion error:', error);
        return value;
    }
};

export const TableBase: React.FC<TableProps> = ({
    columns,
    data,
    title,
    defaultVisibleColumns,
    tableName,
    pageSize = 10,
    defaultSortColumn,
    defaultSortDirection = 'asc',
    onRowClick,
    loading = false,
    menuAction,
    OnExpandedButtonClick,
}) => {
    const theme = useTheme();

    const [state, setState] = useState<TableState>(() => {
        // Try to load saved column state from cookies
        let savedVisibleColumns: string[] = defaultVisibleColumns || columns.map(col => col.key);
        let savedColumnOrder = columns.map(col => col.key);

        if (tableName) {
            const cookieKey = `table_${tableName}_columns`;
            const storedState = cookies.get(cookieKey);
            if (storedState) {
                try {
                    const savedState = JSON.parse(storedState);
                    savedVisibleColumns = savedState.visibleColumns;
                    savedColumnOrder = savedState.orderedColumns;

                    // Ensure all columns exist in the current configuration
                    savedVisibleColumns = savedVisibleColumns.filter(col => columns.some(c => c.key === col));
                    savedColumnOrder = savedColumnOrder.filter(col => columns.some(c => c.key === col));

                    // Add any new columns that weren't in the saved state
                    const savedKeys = new Set(savedColumnOrder);
                    columns.forEach(col => {
                        if (!savedKeys.has(col.key)) {
                            savedColumnOrder.push(col.key);
                        }
                    });
                } catch (error) {
                    console.error('Error parsing saved column state:', error);
                    // Use defaults if there's an error, falling back to all column keys if defaultVisibleColumns is undefined
                    savedVisibleColumns = defaultVisibleColumns || columns.map(col => col.key);
                    savedColumnOrder = columns.map(col => col.key);
                }
            }
        }

        return {
            currentPage: 1,
            pageSize: pageSize,
            sortColumn: defaultSortColumn || '',
            sortDirection: defaultSortDirection === 'desc' ? 'descending' : 'ascending',
            filters: {},
            visibleColumns: savedVisibleColumns || columns.map(col => col.key),
            columnUnits: {},
            searchQuery: '',
            page: 1,
            data: data || [],
            rowsPerPage: pageSize,
            columnOrder: savedColumnOrder
        };
    });

    /*prev represents the previous state object
    ...prev is the spread operator that copies all properties from the previous state
    This is a safe way to update state because:
    It preserves all other state properties (filters, sorting, etc.)
    Only updates the data property
    Without ...prev, you would lose all other state properties
    
    If data prop is: [1, 2, 3]
        data: data || []  // Result: [1, 2, 3]

        // If data prop is: undefined
        data: data || []  // Result: []
    */
    useEffect(() => {
        setState(prev => ({
            ...prev,// Copy all existing state properties
            data: data || [] // Override only the data property
        }));
    }, [data]);

    useEffect(() => {
        if (menuAction?.visible) {
            if (menuAction.type === "expandTable") {
                OnExpandedButtonClick ? OnExpandedButtonClick(true) : null;
            }
            else if (menuAction.type === "columnOptions") {
                setColumnVisibilityModalVisible(true);
            }
        }
    }, [menuAction])

    const [columnVisibilityModalVisible, setColumnVisibilityModalVisible] = useState(false);
    const [unitModalVisible, setUnitModalVisible] = useState(false);
    const [activeColumn, setActiveColumn] = useState<string | null>(null);

    const handleUnitClick = (columnKey: string) => {
        const column = columns.find(col => col.key === columnKey);
        if (!column?.unit) return;

        setActiveColumn(columnKey);
        setUnitModalVisible(true);
    };

    const handleUnitSelect = (unit: string) => {
        if (!activeColumn) return;

        const column = columns.find(col => col.key === activeColumn);
        if (!column?.unit) return;

        // Convert the data for the selected column to the new unit
        const convertedData = data.map(row => {
            const newRow = { ...row };
            const value = parseFloat(row[activeColumn]);
            if (!isNaN(value)) {
                try {
                    const convertedValue = convertValueWithUnit(
                        value,
                        column.unit as ConvertUnit,
                        unit as ConvertUnit
                    );
                    newRow[activeColumn] = convertedValue.toString();
                } catch (error) {
                    console.warn('Error converting value:', error);
                }
            }
            return newRow;
        });

        // Update both the unit and the converted data in state
        setState((prev: TableState): TableState => {
            const updatedColumnUnits = { ...prev.columnUnits };
            updatedColumnUnits[activeColumn] = unit as TableUnit;

            return {
                ...prev,
                columnUnits: updatedColumnUnits,
                data: convertedData,
            };
        });

        setUnitModalVisible(false);
    };

    const handleSearch = (query: string) => {
        setState(prev => ({ ...prev, searchQuery: query, page: 1 }));
    };

    const handleSort = (column: ColumnDefinition) => {
        if (!column.sortable) return;

        setState(prev => ({
            ...prev,
            sortColumn: column.key,
            sortDirection: prev.sortColumn === column.key && prev.sortDirection === 'ascending'
                ? 'descending'
                : 'ascending'
        }));
    };

    const handleFilter = (column: string, value: string) => {
        setState(prev => ({
            ...prev,
            filters: {
                ...prev.filters,
                [column]: value
            }
        }));
    };

    const toggleColumnVisibility = (columnKey: string) => {
        setState(prev => ({
            ...prev,
            visibleColumns: prev.visibleColumns.includes(columnKey)
                ? prev.visibleColumns.filter(key => key !== columnKey)
                : [...prev.visibleColumns, columnKey]
        }));
    };

    const handleColumnReorder = (newOrder: string[]) => {
        setState(prev => ({
            ...prev,
            columnOrder: newOrder
        }));
    };

    const renderCell = (column: ColumnDefinition, row: any) => {
        const value = row[column.key];
        if (value === null || value === undefined) return (
            <DataTable.Cell key={column.key}>
                <Text style={[styles.cellText, { color: theme.colors.onBackground }]}>
                    {''}
                </Text>
            </DataTable.Cell>
        );

        let displayValue = value;
        const selectedUnit = state.columnUnits[column.key];

        if (typeof value === 'number' && column.unit && selectedUnit && selectedUnit !== column.unit) {
            try {
                displayValue = convertValueWithUnit(value, column.unit, selectedUnit);
            } catch (error) {
                console.warn('Error in cell conversion:', error);
                displayValue = value;
            }
        }

        return (
            <DataTable.Cell key={column.key}>
                <Text style={[styles.cellText, { color: theme.colors.onBackground }]}>
                    {typeof displayValue === 'number' ? displayValue.toFixed(2) : displayValue.toString()}
                </Text>
            </DataTable.Cell>
        );
    };

    const renderHeader = (column: ColumnDefinition) => {
        const currentUnit = state.columnUnits[column.key] || column.unit;
        const isSorted = state.sortColumn === column.key;
        const filterValue = state.filters[column.key] || '';

        return (
            <View key={column.key} style={styles.columnHeader}>
                <View style={styles.headerContent}>
                    <TouchableOpacity
                        onPress={() => handleSort(column)}
                        style={styles.headerTitleContainer}
                    >

                        <View style={styles.headerTitleRow}>
                            <Text style={[styles.headerText, { color: theme.colors.onBackground }]} numberOfLines={2}>
                                {column.title}
                            </Text>
                            {isSorted && (
                                <Text style={[styles.sortIcon, { color: theme.colors.primary }]}>
                                    {state.sortDirection === 'ascending' ? '▲' : '▼'}
                                </Text>
                            )}
                        </View>


                    </TouchableOpacity>

                    {column.unit && (
                        <TouchableOpacity
                            onPress={() => handleUnitClick(column.key)}
                            style={styles.unitContainer}
                        >
                            <Text style={[styles.unitText, { color: theme.colors.onBackground }]}>
                                [{currentUnit}]
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>

                {column.filterable && (
                    <View style={styles.filterContainer}>
                        <TextInput
                            placeholder="Filter..."
                            value={filterValue}
                            onChangeText={(text) => handleFilter(column.key, text)}
                            style={[styles.filterInput, { color: theme.colors.onBackground }]}
                            placeholderTextColor={theme.colors.onBackground + '80'}
                        />
                    </View>
                )}
            </View>
        );
    };

    // Process data with current sort, filter, and unit settings
    const processedData = useMemo(() => {
        let result = [...(state.data || [])];

        // Apply filters first
        if (Object.keys(state.filters).length > 0) {
            result = result.filter(item =>
                Object.entries(state.filters).every(([key, filterValue]) => {
                    if (!filterValue) return true;
                    const value = item[key];
                    if (value === null || value === undefined) return false;
                    return value.toString().toLowerCase().includes(filterValue.toLowerCase());
                })
            );
        }

        // Then apply sorting
        if (state.sortColumn) {
            const column = columns.find(col => col.key === state.sortColumn);
            if (column?.sortable) {
                result.sort((a, b) => {
                    let aValue = a[state.sortColumn];
                    let bValue = b[state.sortColumn];

                    const selectedUnit = state.columnUnits[state.sortColumn];
                    if (typeof aValue === 'number' && typeof bValue === 'number' &&
                        column.unit && selectedUnit && selectedUnit !== column.unit) {
                        aValue = convertValueWithUnit(aValue, column.unit, selectedUnit);
                        bValue = convertValueWithUnit(bValue, column.unit, selectedUnit);
                    }

                    if (aValue === bValue) return 0;
                    if (aValue === null || aValue === undefined) return 1;
                    if (bValue === null || bValue === undefined) return -1;

                    const comparison = aValue < bValue ? -1 : 1;
                    return state.sortDirection === 'ascending' ? comparison : -comparison;
                });
            }
        }

        return result;
    }, [state.data, state.filters, state.sortColumn, state.sortDirection, state.columnUnits, columns]);

    // Apply search filter
    const filteredData = useMemo(() => {
        if (!state.searchQuery) {
            return processedData;
        }

        const searchResult = processedData.filter(row =>
            Object.entries(row).some(([key, value]) => {
                if (!state.visibleColumns.includes(key)) return false;
                if (value === null || value === undefined) return false;
                return value.toString().toLowerCase().includes(state.searchQuery.toLowerCase());
            })
        );

        return searchResult;
    }, [processedData, state.searchQuery, state.visibleColumns]);

    const [rowsPerPageInput, setRowsPerPageInput] = useState(state.rowsPerPage.toString());

    // Update input when state changes externally
    useEffect(() => {
        setRowsPerPageInput(state.rowsPerPage.toString());
    }, [state.rowsPerPage]);

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            {/* Table header with actions
            <View style={[styles.tableHeader, {
                backgroundColor: theme.colors.background,
                borderBottomColor: theme.colors.outline
            }]}>
                <View style={styles.headerContainer}>

                    <View style={styles.titleContainer}>
                        <Text variant="titleLarge" style={{ color: theme.colors.onBackground }}>{title}</Text>
                    </View>
                    <View style={styles.actions}>
                        {OnExpandedButtonClick && (
                            <IconButton
                                icon="arrow-expand"
                                onPress={(event) => OnExpandedButtonClick(true)}
                            />
                        )}
                        <IconButton
                            icon="view-column"
                            onPress={() => setColumnVisibilityModalVisible(true)}
                            iconColor={theme.colors.onBackground}
                        />
                    </View>
                </View>
            </View> */}

            {/* Column Visibility Modal */}
            <ColumnVisibilityModal
                visible={columnVisibilityModalVisible}
                onDismiss={() => setColumnVisibilityModalVisible(false)}
                columns={columns.sort((a, b) => {
                    const aIndex = state.columnOrder.indexOf(a.key);
                    const bIndex = state.columnOrder.indexOf(b.key);
                    return aIndex - bIndex;
                })}
                visibleColumns={state.visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onReorderColumns={(newOrder) => {
                    setState(prev => ({
                        ...prev,
                        columnOrder: newOrder
                    }));
                }}
                tableName={tableName}
            />

            {/* Main table content */}
            <ScrollView horizontal>
                <DataTable>
                    {/* Table Headers */}
                    <DataTable.Header style={[styles.tableHeaderRow, {
                        backgroundColor: theme.colors.elevation.level1,
                        borderBottomColor: theme.colors.outline
                    }]}>
                        {columns
                            //Added for the ColumnVisibility
                            .sort((a, b) =>
                                state.columnOrder.indexOf(a.key) - state.columnOrder.indexOf(b.key)
                            )
                            .filter((column) =>
                                state.visibleColumns.includes(column.key)
                            )
                            .map((column) => (
                                <View
                                    key={column.key}
                                    style={[
                                        styles.columnHeader,
                                        {
                                            width: column.width,
                                            minWidth: column.width || 90,
                                            backgroundColor: theme.colors.elevation.level1,
                                            borderBottomColor: theme.colors.outline
                                        }
                                    ]}
                                >
                                    {/* Column Header */}
                                    <View style={styles.headerContent}>
                                        <TouchableOpacity
                                            onPress={() => handleSort(column)}
                                            style={styles.headerTitleContainer}
                                        >
                                            <View style={styles.headerTitleRow}>
                                                <Text style={[styles.headerText, { color: theme.colors.onBackground }]} numberOfLines={2}>
                                                    {column.title}
                                                </Text>
                                                {state.sortColumn === column.key && (
                                                    <Text style={[styles.sortIcon, { color: theme.colors.primary }]}>
                                                        {state.sortDirection === 'ascending' ? '▲' : '▼'}
                                                    </Text>
                                                )}
                                            </View>
                                        </TouchableOpacity>

                                        {/* Unit Display */}
                                        {column.unit && (
                                            <TouchableOpacity
                                                onPress={() => handleUnitClick(column.key)}
                                                style={styles.unitContainer}
                                            >
                                                <Text style={[styles.unitText, {
                                                    color: theme.colors.onBackground,
                                                    backgroundColor: theme.colors.elevation.level3
                                                }]}>
                                                    [{state.columnUnits[column.key] || column.unit}]
                                                </Text>
                                            </TouchableOpacity>
                                        )}
                                    </View>

                                    {/* Filter Section */}
                                    {column.filterable && (
                                        <View style={styles.filterContainer}>
                                            <TextInput
                                                placeholder={`Filter...`}
                                                value={state.filters[column.key] || ''}
                                                onChangeText={(text) => {
                                                    setState(prev => ({
                                                        ...prev,
                                                        filters: {
                                                            ...prev.filters,
                                                            [column.key]: text
                                                        },
                                                        page: 1
                                                    }));
                                                }}
                                                style={[
                                                    styles.filterInput,
                                                    {
                                                        color: theme.colors.onBackground,
                                                        backgroundColor: state.filters[column.key]
                                                            ? theme.colors.elevation.level2
                                                            : 'transparent',
                                                        textAlign: 'center'
                                                    }
                                                ]}
                                                textAlign="center"
                                                placeholderTextColor={theme.colors.onSurfaceDisabled}
                                            />
                                            {state.filters[column.key] && (
                                                <TouchableOpacity
                                                    style={styles.clearFilter}
                                                    onPress={() => {
                                                        setState(prev => ({
                                                            ...prev,
                                                            filters: {
                                                                ...prev.filters,
                                                                [column.key]: ''
                                                            },
                                                            page: 1
                                                        }));
                                                    }}
                                                >
                                                    <Text style={[styles.clearFilterText, { color: theme.colors.onBackground + '80' }]}>×</Text>
                                                </TouchableOpacity>
                                            )}
                                        </View>
                                    )}
                                </View>
                            ))}
                    </DataTable.Header>

                    {/* Table Rows */}
                    {loading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color={theme.colors.primary} />
                        </View>
                    ) : (
                        filteredData
                            .slice((state.page - 1) * state.rowsPerPage, state.page * state.rowsPerPage)
                            .map((row, index) => (
                                <DataTable.Row
                                    key={index}
                                    style={[
                                        styles.row,
                                        {
                                            borderBottomColor: theme.colors.outline,
                                            backgroundColor: index % 2 === 1
                                                ? theme.colors.elevation.level1
                                                : theme.colors.background
                                        }
                                    ]}
                                    onPress={() => onRowClick?.(row)}
                                >
                                    {columns
                                        .filter((column) => state.visibleColumns.includes(column.key))
                                        .map((column) => (
                                            <DataTable.Cell
                                                key={column.key}
                                                style={[
                                                    styles.columnCell,
                                                    {
                                                        width: column.width,
                                                        minWidth: column.width || 90
                                                    }
                                                ]}
                                            >
                                                <Text style={[styles.cellText, { color: theme.colors.onBackground }]}>
                                                    {row[column.key]?.toString() || ''}
                                                </Text>
                                            </DataTable.Cell>
                                        ))}
                                </DataTable.Row>
                            ))
                    )}
                </DataTable>
            </ScrollView>

            {/* Pagination */}
            <View style={[styles.paginationContainer, { backgroundColor: theme.colors.background }]}>
                <View style={styles.rowsPerPageContainer}>
                    <Text style={{ color: theme.colors.onBackground }}>Rows per page: </Text>
                    <TextInput
                        style={[styles.rowsPerPageInput, {
                            backgroundColor: theme.colors.background,
                            color: theme.colors.onBackground,
                            borderColor: theme.colors.outline
                        }]}
                        value={rowsPerPageInput}
                        keyboardType="numeric"
                        onChangeText={(text) => {
                            setRowsPerPageInput(text);
                            const newSize = parseInt(text);
                            if (!isNaN(newSize) && newSize > 0) {
                                setState(prev => ({
                                    ...prev,
                                    rowsPerPage: newSize,
                                    page: 1  // Reset to first page when changing items per page
                                }));
                            }
                        }}
                        onBlur={() => {
                            // When input loses focus, ensure we have a valid number
                            const newSize = parseInt(rowsPerPageInput);
                            if (isNaN(newSize) || newSize <= 0) {
                                setRowsPerPageInput(state.rowsPerPage.toString());
                            }
                        }}
                        placeholderTextColor={theme.colors.onSurfaceDisabled}
                    />
                </View>
                <DataTable.Pagination
                    page={state.page - 1}
                    numberOfPages={Math.ceil(filteredData.length / state.rowsPerPage)}
                    onPageChange={(page) => setState(prev => ({ ...prev, page: page + 1 }))}
                    label={`${(state.page - 1) * state.rowsPerPage + 1}-${Math.min(state.page * state.rowsPerPage, filteredData.length)} of ${filteredData.length}`}
                    showFastPaginationControls
                />
            </View>

            <UnitModal
                visible={unitModalVisible}
                onDismiss={() => {
                    setUnitModalVisible(false);
                    setActiveColumn(null);
                }}
                currentUnit={activeColumn ? (state.columnUnits[activeColumn] || columns.find(col => col.key === activeColumn)?.unit) : undefined}
                onUnitSelect={handleUnitSelect}
                measure={activeColumn ? getMeasureFromUnit(columns.find(col => col.key === activeColumn)?.unit as ConvertUnit) : undefined}
            />


        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    tableHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
        backgroundColor: '#ffffff',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
    },
    titleContainer: {
        flex: 1,
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    tableHeaderRow: {
        backgroundColor: '#f7f7f7',
    },
    columnHeader: {
        flex: 1,
        minHeight: 40,
        maxWidth: 120,
        paddingHorizontal: 2,
        paddingVertical: 4,
        backgroundColor: '#f7f7f7',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    headerContent: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 2,
    },
    headerTitleContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
    },
    headerText: {
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    sortIcon: {
        fontSize: 9,
    },
    filterContainer: {
        width: '100%',
        paddingTop: 1,
    },
    filterInput: {
        height: 22,
        fontSize: 11,
        paddingHorizontal: 2,
        paddingVertical: 0,
    },
    filterInputActive: {
        backgroundColor: '#f5f5f5',
    },
    clearFilter: {
        position: 'absolute',
        right: 2,
        top: 4,
        width: 16,
        height: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    clearFilterText: {
        fontSize: 14,
        color: '#999',
        fontWeight: 'bold',
    },
    unitContainer: {
        marginTop: 1,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 14,
    },
    unitText: {
        fontSize: 10,
        fontWeight: '500',
        paddingHorizontal: 2,
        paddingVertical: 0,
        borderRadius: 2,
    },
    columnCell: {
        flex: 1,
        minHeight: 32,
        maxWidth: 120,
        paddingHorizontal: 2,
        justifyContent: 'center',
        alignItems: 'center',
    },
    cellText: {
        fontSize: 11,
        textAlign: 'center',
    },
    row: {
        minHeight: 32,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    alternateRow: {
        backgroundColor: '#fafafa',
    },
    loadingContainer: {
        padding: 16,
        alignItems: 'center',
    },
    paginationContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 8,
    },
    rowsPerPageContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 16,
    },
    rowsPerPageInput: {
        width: 60,
        height: 30,
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        paddingHorizontal: 8,
        marginLeft: 8,
    },
});
